import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FiSettings, 
  FiSave, 
  FiRefreshCw, 
  FiShield, 
  FiMail, 
  FiDatabase, 
  FiServer, 
  FiUsers, 
  FiEye,
  FiEyeOff,
  FiAlertTriangle,
  FiInfo,
  FiCreditCard,
  FiBook,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiVideo
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const inputClass = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';
const descClass = 'text-xs text-slate-500 mt-1';

const SystemSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showPasswords, setShowPasswords] = useState({});
  const [vtpass, setVtpass] = useState({ mode: 'sandbox', sandbox: {}, live: {} });
  const [vtpassLoading, setVtpassLoading] = useState(true);
  const [vtpassSaving, setVtpassSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectForm, setSubjectForm] = useState(null);
  const [subjectSaving, setSubjectSaving] = useState(false);
  const [testingOpenAI, setTestingOpenAI] = useState(false);
  const [openaiTestResult, setOpenaiTestResult] = useState(null);
  const { showError, showSuccess } = useNotification();

  const EXAM_TYPES = ['WAEC', 'JAMB', 'POST-UTME', 'BECE', 'NECO', 'POST-JAMB', 'JSSCE'];
  const CLASSES = ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3', 'JAMB'];

  const tabs = [
    { id: 'general', label: 'General', icon: FiSettings },
    { id: 'security', label: 'Security', icon: FiShield },
    { id: 'email', label: 'Email', icon: FiMail },
    { id: 'database', label: 'Database', icon: FiDatabase },
    { id: 'api', label: 'API', icon: FiServer },
    { id: 'jitsi', label: 'Meeting Provider', icon: FiVideo },
    { id: 'subjects', label: 'Subject Management', icon: FiBook },
    { id: 'users', label: 'User Management', icon: FiUsers },
    { id: 'vtpass', label: 'VTPass', icon: FiCreditCard }
  ];

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSystemSettings();
      const data = response.data || {};
      if (data.api == null) data.api = {};
      if (typeof data.api.openaiApiKey !== 'string') data.api.openaiApiKey = data.api.openaiApiKey || '';
      if (data.jitsi == null) data.jitsi = { domain: '', appId: '', appSecret: '', useTokenAuth: false };
      setSettings(data);
    } catch (error) {
      showError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchVTPassSettings = useCallback(async () => {
    try {
      setVtpassLoading(true);
      const res = await adminAPI.getVTPassSettings();
      setVtpass(res.data.data || { mode: 'sandbox', sandbox: {}, live: {} });
    } catch (err) {
      showError('Failed to load VTPass settings');
    } finally {
      setVtpassLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchSettings();
    fetchVTPassSettings();
  }, [fetchSettings, fetchVTPassSettings]);

  // Sync active tab with route path/query for deep linking
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const pathToTab = {
      '/admin/security': 'security',
      '/admin/database': 'database',
      '/admin/api': 'api',
      '/admin/jitsi': 'jitsi',
      '/admin/settings': tabParam || 'general'
    };
    const nextTab = pathToTab[location.pathname] || tabParam || 'general';
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [location.pathname, location.search, activeTab]);

  // fetchSettings memoized above

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
    setUnsavedChanges(true);
  };

  // fetchVTPassSettings memoized above

  const handleVTPassFieldChange = (env, field, value) => {
    setVtpass(prev => ({
      ...prev,
      [env]: {
        ...prev[env],
        [field]: value
      }
    }));
    setUnsavedChanges(true);
  };

  const handleVTPassModeChange = (mode) => {
    setVtpass(prev => ({ ...prev, mode }));
    setUnsavedChanges(true);
  };

  const saveVTPassSettings = async () => {
    try {
      setVtpassSaving(true);
      const payload = {
        mode: vtpass.mode,
        sandbox: vtpass.sandbox,
        live: vtpass.live
      };
      await adminAPI.updateVTPassSettings(payload);
      setUnsavedChanges(false);
      showSuccess('VTPass settings saved');
    } catch (err) {
      showError('Failed to save VTPass settings');
    } finally {
      setVtpassSaving(false);
    }
  };

  const setVTPassMode = async () => {
    try {
      setVtpassSaving(true);
      await adminAPI.setVTPassMode(vtpass.mode);
      showSuccess(`VTPass mode set to ${vtpass.mode}`);
    } catch (err) {
      showError('Failed to set VTPass mode');
    } finally {
      setVtpassSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await adminAPI.updateSystemSettings(settings);
      setUnsavedChanges(false);
      showSuccess('Settings saved successfully');
    } catch (error) {
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = async () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      try {
        const response = await adminAPI.resetSystemSettings();
        setSettings(response.data);
        setUnsavedChanges(false);
        showSuccess('Settings reset to default values');
      } catch (error) {
        showError('Failed to reset settings');
      }
    }
  };

  const handleTestConnection = async (type, options = {}) => {
    if (type === 'openai') {
      setTestingOpenAI(true);
      setOpenaiTestResult(null);
    }
    try {
      const response = await adminAPI.testConnection(type, options);
      if (type === 'openai') {
        setOpenaiTestResult({
          success: response.data.success,
          message: response.data.success ? (response.data.message || 'Expert Intelligence API responded successfully.') : (response.data.error || 'Unknown error'),
          openaiResponse: response.data.openaiResponse ?? response.data.rawContent,
          model: response.data.model,
          usage: response.data.usage,
          finishReason: response.data.finishReason
        });
      }
      if (response.data.success) {
        showSuccess(type === 'openai' ? 'Expert Intelligence API key is valid and responding.' : `${type} connection test successful`);
      } else {
        showError(`${type === 'openai' ? 'Expert Intelligence' : type} test failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || `Failed to test ${type} connection`;
      if (type === 'openai') {
        setOpenaiTestResult({ success: false, message: msg, openaiResponse: error.response?.data?.openaiResponse, model: null });
      }
      showError(msg);
    } finally {
      if (type === 'openai') setTestingOpenAI(false);
    }
  };

  const fetchSubjects = useCallback(async () => {
    try {
      setSubjectsLoading(true);
      const res = await adminAPI.getSubjects();
      setSubjects(res.data?.data || []);
    } catch (err) {
      showError('Failed to load subjects');
      setSubjects([]);
    } finally {
      setSubjectsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (activeTab === 'subjects') fetchSubjects();
  }, [activeTab, fetchSubjects]);

  const saveSubject = async () => {
    if (!subjectForm?.name?.trim() || !subjectForm?.examType || !subjectForm?.class) {
      showError('Name, exam type and class are required');
      return;
    }
    try {
      setSubjectSaving(true);
      if (subjectForm._id) {
        await adminAPI.updateSubject(subjectForm._id, {
          name: subjectForm.name.trim(),
          examType: subjectForm.examType,
          class: subjectForm.class,
          isActive: subjectForm.isActive !== false,
          order: subjectForm.order || 0
        });
        showSuccess('Subject updated');
      } else {
        await adminAPI.createSubject({
          name: subjectForm.name.trim(),
          examType: subjectForm.examType,
          class: subjectForm.class,
          isActive: subjectForm.isActive !== false,
          order: subjectForm.order || 0
        });
        showSuccess('Subject added');
      }
      setSubjectForm(null);
      fetchSubjects();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save subject');
    } finally {
      setSubjectSaving(false);
    }
  };

  const deleteSubject = async (id) => {
    if (!window.confirm('Remove this subject from settings? It will no longer appear in exam/test dropdowns.')) return;
    try {
      await adminAPI.deleteSubject(id);
      showSuccess('Subject removed');
      fetchSubjects();
      if (subjectForm?._id === id) setSubjectForm(null);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete subject');
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const renderGeneralSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">General Settings</h3>
      <div>
        <label className={labelClass}>Application Name</label>
        <input type="text" value={settings?.general?.appName || ''} onChange={(e) => handleSettingChange('general', 'appName', e.target.value)} className={inputClass} />
        <p className={descClass}>The name displayed in the application header</p>
      </div>
      <div>
        <label className={labelClass}>Application Description</label>
        <textarea value={settings?.general?.appDescription || ''} onChange={(e) => handleSettingChange('general', 'appDescription', e.target.value)} className={`${inputClass} resize-y`} rows={3} />
        <p className={descClass}>Brief description of the application</p>
      </div>
      <div>
        <label className={labelClass}>Default Language</label>
        <select value={settings?.general?.defaultLanguage || 'en'} onChange={(e) => handleSettingChange('general', 'defaultLanguage', e.target.value)} className={inputClass}>
          <option value="en">English</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
          <option value="de">German</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Timezone</label>
        <select value={settings?.general?.timezone || 'UTC'} onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)} className={inputClass}>
          <option value="UTC">UTC</option>
          <option value="America/New_York">Eastern Time</option>
          <option value="America/Chicago">Central Time</option>
          <option value="America/Denver">Mountain Time</option>
          <option value="America/Los_Angeles">Pacific Time</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.general?.maintenanceMode || false} onChange={(e) => handleSettingChange('general', 'maintenanceMode', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Maintenance Mode</label>
      </div>
      <p className={descClass}>Enable to show maintenance page to users</p>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.general?.allowRegistration !== false} onChange={(e) => handleSettingChange('general', 'allowRegistration', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Allow User Registration</label>
      </div>
      <p className={descClass}>Allow new users to register accounts</p>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">Security Settings</h3>
      <div>
        <label className={labelClass}>Session Timeout (minutes)</label>
        <input type="number" value={settings?.security?.sessionTimeout || 30} onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value, 10) || 30)} className={inputClass} min={5} max={1440} />
        <p className={descClass}>Automatic logout after inactivity</p>
      </div>
      <div>
        <label className={labelClass}>Password Minimum Length</label>
        <input type="number" value={settings?.security?.passwordMinLength || 8} onChange={(e) => handleSettingChange('security', 'passwordMinLength', parseInt(e.target.value, 10) || 8)} className={inputClass} min={6} max={32} />
      </div>
      <div>
        <label className={labelClass}>Max Login Attempts</label>
        <input type="number" value={settings?.security?.maxLoginAttempts || 5} onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value, 10) || 5)} className={inputClass} min={3} max={10} />
        <p className={descClass}>Account lockout after failed attempts</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.security?.requireTwoFactor || false} onChange={(e) => handleSettingChange('security', 'requireTwoFactor', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Require Two-Factor Authentication</label>
      </div>
      <p className={descClass}>Mandatory 2FA for all users</p>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.security?.passwordComplexity !== false} onChange={(e) => handleSettingChange('security', 'passwordComplexity', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Enforce Password Complexity</label>
      </div>
      <p className={descClass}>Require uppercase, lowercase, numbers, and symbols</p>
      <div>
        <label className={labelClass}>JWT Secret Key</label>
        <div className="flex gap-2">
          <input type={showPasswords.jwtSecret ? 'text' : 'password'} value={settings?.security?.jwtSecret || ''} onChange={(e) => handleSettingChange('security', 'jwtSecret', e.target.value)} className={inputClass} />
          <button type="button" onClick={() => togglePasswordVisibility('jwtSecret')} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 shrink-0">{showPasswords.jwtSecret ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}</button>
        </div>
        <p className={descClass}>Secret key for JWT token signing</p>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">Email Settings</h3>
      <div>
        <label className={labelClass}>Email Provider</label>
        <select value={settings?.email?.provider || 'resend'} onChange={(e) => handleSettingChange('email', 'provider', e.target.value)} className={inputClass}>
          <option value="resend">Resend</option>
          <option value="smtp">SMTP</option>
        </select>
        <p className={descClass}>Choose the email delivery provider.</p>
      </div>
      {(settings?.email?.provider || 'resend') === 'smtp' ? (
        <>
          <div><label className={labelClass}>SMTP Host</label><input type="text" value={settings?.email?.smtpHost || ''} onChange={(e) => handleSettingChange('email', 'smtpHost', e.target.value)} className={inputClass} placeholder="smtp.gmail.com" /></div>
          <div><label className={labelClass}>SMTP Port</label><input type="number" value={settings?.email?.smtpPort ?? 587} onChange={(e) => handleSettingChange('email', 'smtpPort', parseInt(e.target.value, 10) || 587)} className={inputClass} /></div>
          <div><label className={labelClass}>SMTP Username</label><input type="text" value={settings?.email?.smtpUsername || ''} onChange={(e) => handleSettingChange('email', 'smtpUsername', e.target.value)} className={inputClass} /></div>
          <div>
            <label className={labelClass}>SMTP Password</label>
            <div className="flex gap-2">
              <input type={showPasswords.smtpPassword ? 'text' : 'password'} value={settings?.email?.smtpPassword || ''} onChange={(e) => handleSettingChange('email', 'smtpPassword', e.target.value)} className={inputClass} />
              <button type="button" onClick={() => togglePasswordVisibility('smtpPassword')} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 shrink-0">{showPasswords.smtpPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}</button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={settings?.email?.enableSSL !== false} onChange={(e) => handleSettingChange('email', 'enableSSL', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
            <label className="text-sm font-medium text-slate-700">Enable SSL/TLS</label>
          </div>
        </>
      ) : (
        <div>
          <label className={labelClass}>Resend API Key</label>
          <div className="flex gap-2">
            <input type={showPasswords.resendApiKey ? 'text' : 'password'} value={settings?.email?.resendApiKey || ''} onChange={(e) => handleSettingChange('email', 'resendApiKey', e.target.value)} className={inputClass} placeholder="re_..." />
            <button type="button" onClick={() => togglePasswordVisibility('resendApiKey')} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 shrink-0">{showPasswords.resendApiKey ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}</button>
          </div>
          <p className={descClass}>Get your API key from resend.com dashboard.</p>
        </div>
      )}
      <div><label className={labelClass}>From Email</label><input type="email" value={settings?.email?.fromEmail || ''} onChange={(e) => handleSettingChange('email', 'fromEmail', e.target.value)} className={inputClass} placeholder="noreply@passam.com" /></div>
      <div><label className={labelClass}>From Name</label><input type="text" value={settings?.email?.fromName || ''} onChange={(e) => handleSettingChange('email', 'fromName', e.target.value)} className={inputClass} placeholder="PassQik Platform" /></div>
      <div>
        <label className={labelClass}>Test Recipient Email</label>
        <input type="email" value={settings?.email?.testRecipient || ''} onChange={(e) => handleSettingChange('email', 'testRecipient', e.target.value)} className={inputClass} placeholder="you@example.com" />
        <p className={descClass}>Optional. If set, test emails send to this address.</p>
      </div>
      <div className="pt-2">
        <button type="button" onClick={() => handleTestConnection('email')} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50">Test Email Connection</button>
      </div>
    </div>
  );

  const renderDatabaseSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">Database Settings</h3>
      <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
        <FiAlertTriangle className="h-4 w-4 shrink-0" />
        <span>Changing database settings requires application restart.</span>
      </div>
      <div>
        <label className={labelClass}>Database Host</label>
        <input type="text" value={settings?.database?.host || ''} onChange={(e) => handleSettingChange('database', 'host', e.target.value)} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Database Port</label>
        <input type="number" value={settings?.database?.port ?? 5432} onChange={(e) => handleSettingChange('database', 'port', parseInt(e.target.value, 10) || 5432)} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Database Name</label>
        <input type="text" value={settings?.database?.name || ''} onChange={(e) => handleSettingChange('database', 'name', e.target.value)} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Database Username</label>
        <input type="text" value={settings?.database?.username || ''} onChange={(e) => handleSettingChange('database', 'username', e.target.value)} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Database Password</label>
        <div className="flex gap-2">
          <input type={showPasswords.dbPassword ? 'text' : 'password'} value={settings?.database?.password || ''} onChange={(e) => handleSettingChange('database', 'password', e.target.value)} className={inputClass} />
          <button type="button" onClick={() => togglePasswordVisibility('dbPassword')} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 shrink-0">{showPasswords.dbPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}</button>
        </div>
      </div>
      <div>
        <label className={labelClass}>Connection Pool Size</label>
        <input type="number" value={settings?.database?.poolSize ?? 10} onChange={(e) => handleSettingChange('database', 'poolSize', parseInt(e.target.value, 10) || 10)} className={inputClass} min={1} max={100} />
      </div>
      <div className="pt-2">
        <button type="button" onClick={() => handleTestConnection('database')} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50">Test Database Connection</button>
      </div>
    </div>
  );

  const renderApiSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">API Settings</h3>
      <div>
        <label className={labelClass}>Expert Intelligence API Key</label>
        <div className="flex gap-2">
          <input type={showPasswords.openaiApiKey ? 'text' : 'password'} value={settings?.api?.openaiApiKey || ''} onChange={(e) => handleSettingChange('api', 'openaiApiKey', e.target.value)} className={inputClass} placeholder="sk-..." />
          <button type="button" onClick={() => togglePasswordVisibility('openaiApiKey')} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 shrink-0">{showPasswords.openaiApiKey ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}</button>
        </div>
        <p className={descClass}>Used for AI question generation, syllabus processing, and insights. Saved here or set OPENAI_API_KEY in env.</p>
      </div>
      <div>
        <label className={labelClass}>API Rate Limit (requests per minute)</label>
        <input type="number" value={settings?.api?.rateLimit ?? 100} onChange={(e) => handleSettingChange('api', 'rateLimit', parseInt(e.target.value, 10) || 100)} className={inputClass} min={10} max={1000} />
      </div>
      <div>
        <label className={labelClass}>API Timeout (seconds)</label>
        <input type="number" value={settings?.api?.timeout ?? 30} onChange={(e) => handleSettingChange('api', 'timeout', parseInt(e.target.value, 10) || 30)} className={inputClass} min={5} max={300} />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.api?.enableCors !== false} onChange={(e) => handleSettingChange('api', 'enableCors', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Enable CORS</label>
      </div>
      <p className={descClass}>Allow cross-origin requests</p>
      <div>
        <label className={labelClass}>Allowed Origins</label>
        <textarea value={(settings?.api?.allowedOrigins || []).join('\n')} onChange={(e) => handleSettingChange('api', 'allowedOrigins', e.target.value.split('\n').filter(Boolean))} className={`${inputClass} resize-y`} rows={4} placeholder={'https://example.com\nhttps://app.example.com'} />
        <p className={descClass}>One origin per line</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.api?.enableLogging !== false} onChange={(e) => handleSettingChange('api', 'enableLogging', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Enable API Logging</label>
      </div>
      <p className={descClass}>Log all API requests and responses</p>
      <div className="pt-2">
        <button type="button" onClick={() => handleTestConnection('openai')} disabled={testingOpenAI} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50">
          {testingOpenAI ? 'Testing…' : 'Test Expert Intelligence Key'}
        </button>
        <p className={`${descClass} mt-1`}>Save settings first to store the key. Test uses the key saved in the database (or OPENAI_API_KEY env).</p>
      </div>
      {openaiTestResult != null && (
        <div className={`p-3 rounded-lg text-sm font-medium ${openaiTestResult.success ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          <div>{openaiTestResult.success ? '✓ ' : '✗ '}{openaiTestResult.message}</div>
          {openaiTestResult.openaiResponse != null && openaiTestResult.openaiResponse !== '' && (
            <div className="mt-2 p-2 bg-black/5 rounded text-slate-700">
              <strong>Actual response from Expert Intelligence:</strong>
              <pre className="mt-1 font-mono text-xs whitespace-pre-wrap break-all">{openaiTestResult.openaiResponse}</pre>
            </div>
          )}
          {openaiTestResult.model && <div className="mt-1 text-xs opacity-90">Model: {openaiTestResult.model}{openaiTestResult.finishReason ? ` · Finish: ${openaiTestResult.finishReason}` : ''}</div>}
          {openaiTestResult.usage && <div className="mt-1 text-xs opacity-90">Tokens: prompt {openaiTestResult.usage.promptTokens} → completion {openaiTestResult.usage.completionTokens} (total {openaiTestResult.usage.totalTokens})</div>}
        </div>
      )}
    </div>
  );

  const renderJitsiSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">Meeting Provider</h3>
      <p className="text-sm text-slate-500">Configure the meeting backend used for teacher-student sessions and parent-teacher or staff meetings. These fields still power the Jitsi fallback path, while Bunch is selected through the server-side meeting provider configuration.</p>
      <div>
        <label className={labelClass}>Jitsi fallback domain</label>
        <input type="text" value={settings?.jitsi?.domain || ''} onChange={(e) => handleSettingChange('jitsi', 'domain', e.target.value)} className={inputClass} placeholder="meet.jit.si or meet.yourdomain.com" />
        <p className={descClass}>Hostname only, no https:// (e.g. meet.jit.si)</p>
      </div>
      <div>
        <label className={labelClass}>JWT App ID</label>
        <input type="text" value={settings?.jitsi?.appId || ''} onChange={(e) => handleSettingChange('jitsi', 'appId', e.target.value)} className={inputClass} placeholder="passam-app" />
        <p className={descClass}>Used only for the legacy Jitsi JWT fallback path.</p>
      </div>
      <div>
        <label className={labelClass}>JWT App secret</label>
        <div className="flex gap-2">
          <input type={showPasswords.jitsiSecret ? 'text' : 'password'} value={settings?.jitsi?.appSecret || ''} onChange={(e) => handleSettingChange('jitsi', 'appSecret', e.target.value)} className={inputClass} placeholder="Leave empty to use JITSI_SECRET env" />
          <button type="button" onClick={() => togglePasswordVisibility('jitsiSecret')} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 shrink-0">{showPasswords.jitsiSecret ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}</button>
        </div>
        <p className={descClass}>Used only when the configured meeting provider falls back to Jitsi token authentication.</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.jitsi?.useTokenAuth || false} onChange={(e) => handleSettingChange('jitsi', 'useTokenAuth', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Use token authentication</label>
      </div>
      <p className={descClass}>Enable when your Jitsi fallback server requires JWT authentication.</p>
    </div>
  );

  const renderSubjectManagement = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">Subject Management</h3>
      <p className="text-sm text-slate-500">Define subjects and link each to an exam type and class. These appear in exam selection, test creation, games, and syllabus.</p>
      {subjectForm ? (
        <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3">
          <h4 className="text-sm font-medium text-slate-900">{subjectForm._id ? 'Edit subject' : 'Add subject'}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Name</label>
              <input type="text" value={subjectForm.name || ''} onChange={(e) => setSubjectForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} placeholder="e.g. Mathematics" />
            </div>
            <div>
              <label className={labelClass}>Exam type</label>
              <select value={subjectForm.examType || ''} onChange={(e) => setSubjectForm((f) => ({ ...f, examType: e.target.value }))} className={inputClass}>
                <option value="">Select exam</option>
                {EXAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Class</label>
              <select value={subjectForm.class || ''} onChange={(e) => setSubjectForm((f) => ({ ...f, class: e.target.value }))} className={inputClass}>
                <option value="">Select class</option>
                {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={saveSubject} disabled={subjectSaving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50">{subjectSaving ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => setSubjectForm(null)} className="px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="pb-2">
          <button type="button" onClick={() => setSubjectForm({ name: '', examType: '', class: '', isActive: true, order: 0 })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800">
            <FiPlus className="h-4 w-4" /> Add subject
          </button>
        </div>
      )}
      {subjectsLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80">
                <th className="px-4 py-3 text-left font-medium text-slate-700">Subject</th>
                <th className="px-4 py-3 text-left font-medium text-slate-700">Exam</th>
                <th className="px-4 py-3 text-left font-medium text-slate-700">Class</th>
                <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                <th className="px-4 py-3 w-0" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {subjects.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No subjects configured. Add subjects to have them appear in exam and test dropdowns.</td></tr>
              ) : (
                subjects.map((s) => (
                  <tr key={s._id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                    <td className="px-4 py-3 text-slate-600">{s.examType}</td>
                    <td className="px-4 py-3 text-slate-600">{s.class}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.isActive !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{s.isActive !== false ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setSubjectForm({ _id: s._id, name: s.name, examType: s.examType, class: s.class, isActive: s.isActive, order: s.order })} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Edit"><FiEdit2 className="h-4 w-4" /></button>
                        <button type="button" onClick={() => deleteSubject(s._id)} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete"><FiTrash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderVTPassSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">VTPass Settings</h3>
      {vtpassLoading ? (
        <p className="text-sm text-slate-500">Loading VTPass settings…</p>
      ) : (
        <>
          <div>
            <label className={labelClass}>Active Mode</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="vtpass-mode" checked={vtpass.mode === 'sandbox'} onChange={() => handleVTPassModeChange('sandbox')} className="text-slate-900 focus:ring-slate-400" />
                <span className="text-sm font-medium text-slate-700">Sandbox</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="vtpass-mode" checked={vtpass.mode === 'live'} onChange={() => handleVTPassModeChange('live')} className="text-slate-900 focus:ring-slate-400" />
                <span className="text-sm font-medium text-slate-700">Live</span>
              </label>
            </div>
            <p className={descClass}>Toggle the active environment used by rewards</p>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-800">Sandbox Credentials</h4>
            <div><label className={labelClass}>Username</label><input type="text" value={vtpass?.sandbox?.username || ''} onChange={(e) => handleVTPassFieldChange('sandbox', 'username', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Password</label><input type="password" value={vtpass?.sandbox?.password || ''} onChange={(e) => handleVTPassFieldChange('sandbox', 'password', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Public Key</label><input type="text" value={vtpass?.sandbox?.publicKey || ''} onChange={(e) => handleVTPassFieldChange('sandbox', 'publicKey', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Secret Key</label><input type="text" value={vtpass?.sandbox?.secretKey || ''} onChange={(e) => handleVTPassFieldChange('sandbox', 'secretKey', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Base URL</label><input type="text" value={vtpass?.sandbox?.baseURL || ''} onChange={(e) => handleVTPassFieldChange('sandbox', 'baseURL', e.target.value)} className={inputClass} placeholder="https://sandbox.vtpass.com/api/pay" /></div>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-800">Live Credentials</h4>
            <div><label className={labelClass}>Username</label><input type="text" value={vtpass?.live?.username || ''} onChange={(e) => handleVTPassFieldChange('live', 'username', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Password</label><input type="password" value={vtpass?.live?.password || ''} onChange={(e) => handleVTPassFieldChange('live', 'password', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Public Key</label><input type="text" value={vtpass?.live?.publicKey || ''} onChange={(e) => handleVTPassFieldChange('live', 'publicKey', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Secret Key</label><input type="text" value={vtpass?.live?.secretKey || ''} onChange={(e) => handleVTPassFieldChange('live', 'secretKey', e.target.value)} className={inputClass} /></div>
            <div><label className={labelClass}>Base URL</label><input type="text" value={vtpass?.live?.baseURL || ''} onChange={(e) => handleVTPassFieldChange('live', 'baseURL', e.target.value)} className={inputClass} placeholder="https://vtpass.com/api/pay" /></div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={saveVTPassSettings} disabled={vtpassSaving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50">{vtpassSaving ? 'Saving…' : 'Save VTPass Settings'}</button>
            <button type="button" onClick={setVTPassMode} disabled={vtpassSaving} className="px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50">Set Active Mode</button>
          </div>
        </>
      )}
    </div>
  );

  const renderUserSettings = () => (
    <div className="space-y-5">
      <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2">User Management Settings</h3>
      <div>
        <label className={labelClass}>Default User Role</label>
        <select value={settings?.users?.defaultRole || 'student'} onChange={(e) => handleSettingChange('users', 'defaultRole', e.target.value)} className={inputClass}>
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
          <option value="parent">Parent</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.users?.requireEmailVerification !== false} onChange={(e) => handleSettingChange('users', 'requireEmailVerification', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Require Email Verification</label>
      </div>
      <p className={descClass}>Users must verify email before accessing the platform</p>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={settings?.users?.allowProfilePictures !== false} onChange={(e) => handleSettingChange('users', 'allowProfilePictures', e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
        <label className="text-sm font-medium text-slate-700">Allow Profile Pictures</label>
      </div>
      <div>
        <label className={labelClass}>Max File Upload Size (MB)</label>
        <input type="number" value={settings?.users?.maxUploadSize ?? 5} onChange={(e) => handleSettingChange('users', 'maxUploadSize', parseInt(e.target.value, 10) || 5)} className={inputClass} min={1} max={100} />
      </div>
      <div>
        <label className={labelClass}>Account Deletion Grace Period (days)</label>
        <input type="number" value={settings?.users?.deletionGracePeriod ?? 30} onChange={(e) => handleSettingChange('users', 'deletionGracePeriod', parseInt(e.target.value, 10) || 30)} className={inputClass} min={0} max={365} />
        <p className={descClass}>Days before permanently deleting user accounts</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const tabToPath = {
    general: '/admin/settings',
    security: '/admin/security',
    email: '/admin/settings?tab=email',
    database: '/admin/database',
    api: '/admin/api',
    jitsi: '/admin/jitsi',
    subjects: '/admin/settings?tab=subjects',
    users: '/admin/settings?tab=users',
    vtpass: '/admin/settings?tab=vtpass'
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">System Settings</h1>
            <p className="text-sm text-slate-500 mt-0.5">Configure system-wide settings and preferences.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {unsavedChanges && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-800">
                <FiInfo className="h-3.5 w-3.5" />
                Unsaved changes
              </span>
            )}
            <button type="button" onClick={handleResetSettings} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
              <FiRefreshCw className="h-4 w-4" />
              Reset to defaults
            </button>
            <button type="button" onClick={handleSaveSettings} disabled={saving || !unsavedChanges} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:pointer-events-none transition-colors shadow-sm">
              {saving ? <LoadingSpinner /> : <FiSave className="h-4 w-4" />}
              Save settings
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <nav className="lg:w-56 shrink-0 flex flex-row lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${isActive ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => {
                    navigate(tabToPath[tab.id] || '/admin/settings');
                    setActiveTab(tab.id);
                  }}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            {activeTab === 'general' && renderGeneralSettings()}
            {activeTab === 'security' && renderSecuritySettings()}
            {activeTab === 'email' && renderEmailSettings()}
            {activeTab === 'database' && renderDatabaseSettings()}
            {activeTab === 'api' && renderApiSettings()}
            {activeTab === 'jitsi' && renderJitsiSettings()}
            {activeTab === 'subjects' && renderSubjectManagement()}
            {activeTab === 'users' && renderUserSettings()}
            {activeTab === 'vtpass' && renderVTPassSettings()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;